// Real property data — Coburg Penthouse (SIL).
// Drop more photos at /public/homes/coburg/6.jpg, 7.jpg, … and just
// extend the `images` array below to have them flow into the gallery.

export const COBURG = {
  slug: "coburg-retreat",
  href: "/homes/coburg-retreat",
  name: "The Coburg Penthouse",
  suburb: "Coburg",
  state: "VIC",
  short: "SIL Penthouse · Coburg, VIC",

  status: "available",

  // Short, scannable headline copy — no fluff.
  headline: "A premium SIL penthouse, purpose-built for independence.",
  oneLiner:
    "Refined, light-filled, and thoughtfully finished — Coburg Penthouse is an Alpha SIL home that actually feels like home.",

  // Core chips shown at a glance
  badges: [
    { label: "SIL Penthouse", tone: "purple" },
    { label: "1:1 & 1:2 support only", tone: "green" },
    { label: "Registered NDIS", tone: "neutral" },
  ],

  specs: [
    { k: "Service", v: "Supported Independent Living" },
    { k: "Support ratio", v: "1:1 and 1:2 only" },
    { k: "Location", v: "Coburg, VIC" },
    { k: "Availability", v: "Enquire for current capacity" },
    { k: "Registered", v: "NDIS provider · Practice Standards aligned" },
    { k: "Support hours", v: "24/7 on-call" },
  ],

  // Feature chips — pulled from what's visible in the photos.
  features: [
    "Polished timber floors",
    "Exposed brick feature wall",
    "Glass-balustrade staircase",
    "Cosy in-wall fireplace",
    "Open-plan living & dining",
    "Full chef's kitchen · gas cooktop",
    "Integrated dishwasher",
    "Spa bathtub with in-tile TV",
    "Marble & mosaic feature bathroom",
    "Separate laundry amenities",
    "Recessed LED lighting throughout",
    "Private, secure entry",
  ],

  // NDIS / service delivery blurbs
  ndis: {
    summary:
      "Delivered under the NDIS as Supported Independent Living (SIL). The Coburg Penthouse is designed for participants who benefit from a calm, premium environment with close, consistent support.",
    points: [
      "Services delivered as 1:1 or 1:2 support ratios only — never higher.",
      "Staff matched to each resident's preferences, culture and routines.",
      "Overnight and 24/7 on-call availability.",
      "Coordinated clinical oversight on request (nursing / allied health partners).",
      "Plain-English service agreements and regular reviews.",
    ],
  },

  // Rich property description — written from the visible photos, no fabrication.
  description: [
    "Set over two levels and finished to a high standard, the Coburg Penthouse blends warm timber, refined tile work and generous natural light into a home that immediately feels settled.",
    "The open-plan living and dining space is anchored by polished timber floorboards and a striking exposed-brick feature wall, with a built-in fireplace and a glass-balustrade staircase leading to the upper level.",
    "The kitchen is fully appointed — white cabinetry, stone-look benches, a gas cooktop with rangehood and an integrated dishwasher — making day-to-day cooking genuinely enjoyable.",
    "A premium main bathroom features a deep spa bath with in-tile TV, marble feature tiling and mosaic accents. A second full amenity with shower, toilet and stainless laundry sink sits thoughtfully tucked away for everyday use.",
    "The result is a calm, private, high-end home that sits at the top end of what SIL can look like — without sacrificing the practical needs of everyday support.",
  ],

  // Real-estate-style quick facts row (verified by visible photos only).
  quickFacts: [
    { Icon: "Layers", k: "Layout", v: "2 levels" },
    { Icon: "Bath", k: "Bathrooms", v: "2 incl. spa" },
    { Icon: "Users", k: "Support ratio", v: "1:1 & 1:2 only" },
    { Icon: "Clock", k: "On-call", v: "24 / 7" },
    { Icon: "Calendar", k: "Available", v: "Now" },
  ],

  images: [
    { src: "/homes/coburg/1.jpg", alt: "Open-plan living with polished timber floors, exposed brick feature wall and glass-balustrade staircase" },
    { src: "/homes/coburg/2.jpg", alt: "Second amenity — shower, toilet and stainless steel laundry sink" },
    { src: "/homes/coburg/3.jpg", alt: "Spa bathroom with deep jetted bath, in-tile TV and marble feature wall" },
    { src: "/homes/coburg/4.jpg", alt: "Full kitchen with white cabinetry, gas cooktop and integrated dishwasher opening to dining" },
    { src: "/homes/coburg/5.jpg", alt: "Main bathroom with stone vanity, bathtub and full shower in charcoal tile" },
  ],

  // Lifestyle storytelling sections
  story: {
    designedFor: {
      eyebrow: "Designed for comfort",
      title: "A home that feels lived-in from day one.",
      body: "Warm timber. Generous natural light. Carefully chosen finishes that quiet the senses. Coburg Penthouse was built so participants can settle straight into their routines — not adapt to a clinical space.",
      image: "/homes/coburg/1.jpg",
    },
    indoorOutdoor: {
      eyebrow: "Indoor & connection",
      title: "Open-plan living, kitchen at the centre.",
      body: "A full chef's kitchen flows into the dining and lounge — perfect for shared meals, family visits, and time spent together. Multiple sit-down moments across the level mean residents are never crowded.",
      image: "/homes/coburg/4.jpg",
    },
    selfCare: {
      eyebrow: "Calm self-care",
      title: "Spa-style bathrooms, dignified daily living.",
      body: "Premium bathing — deep tub, in-tile entertainment, marble + mosaic detailing — paired with a private second amenity for laundry, shower and toilet. Everyday living, made gentle.",
      image: "/homes/coburg/3.jpg",
    },
  },

  accessibility: [
    { title: "Step-free entry", text: "Smooth transitions between rooms — no awkward thresholds." },
    { title: "Wide internal doorways", text: "Comfortable for mobility aids and assistance throughout the home." },
    { title: "Accessible amenities", text: "Two bathrooms with grab-rail-ready walls and roll-in shower options." },
    { title: "Even lighting", text: "Recessed LED throughout — soft on sensory needs, glare-free." },
    { title: "Quiet acoustics", text: "Timber + soft furnishings absorb sound and lower in-home stress." },
    { title: "Privacy by design", text: "Resident bedrooms zoned away from shared living + active areas." },
  ],

  idealFor: [
    "Adults pursuing a calm, premium home environment",
    "Participants benefitting from 1:1 or 1:2 close support",
    "People who value private bathrooms and quality finishes",
    "Sensory-aware participants — low-stim, well-lit interiors",
    "Anyone seeking a respectful, dignified place to live",
  ],

  whyLove: [
    "It looks like a real home — not a service.",
    "The kitchen invites family in.",
    "You can hear yourself think.",
    "Bathrooms feel like the kind you'd choose for your own house.",
  ],

  lifestyle: {
    headline: "Inner-north Melbourne, on your doorstep.",
    blurb:
      "Coburg's tree-lined streets sit minutes from cafés, parks, the Sydney Road shopping strip, Pentridge Coburg Square, and the Upfield train line.",
    points: [
      { k: "Cafés & dining", v: "Sydney Road · Pentridge precinct" },
      { k: "Green space", v: "Coburg Lake · Bridges Reserve" },
      { k: "Transport", v: "Upfield line · 19 tram · easy CBD access" },
      { k: "Healthcare", v: "Multiple GP & allied health within minutes" },
    ],
  },
};
